import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/useAuth';

export function AppHeader() {
  const { isAuthenticated, logout } = useAuth();
  const { pathname } = useLocation();
  // "My Plan" spans the whole plan context, not just /plan itself.
  const isPlanContext =
    pathname === '/plan' ||
    pathname.startsWith('/plans/') ||
    pathname.startsWith('/projections/') ||
    pathname === '/comparison';
  // API docs live under /api-docs but belong to the API Keys area.
  const isApiContext = pathname === '/account/api-keys' || pathname === '/api-docs';
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    void navigate('/');
  };

  const closeMenu = () => setMenuOpen(false);

  // Close menu on route change (browser back/forward, programmatic navigate)
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Escape key closes menu (WCAG overlay dismissal)
  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  // Only show hamburger when authenticated (3+ nav items)
  const showHamburger = isAuthenticated;

  return (
    <>
      {showHamburger && (
        <div
          className={`app-header-backdrop${menuOpen ? ' app-header-backdrop--open' : ''}`}
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      <header className="app-header">
        <div className="app-header-content">
          <div className="app-header-left">
            <NavLink to="/" className="app-header-brand" onClick={closeMenu}>
              {/* The Horizon mark — same geometry as the app icon masters
                  (AppIcon.appiconset) and GPLogoMark on iOS. Night-variant
                  ground #0A0F1F + hairline edge so the lower half doesn't
                  dissolve into the page background. */}
              <svg className="app-header-brand-icon" viewBox="0 0 1024 1024" width="24" height="24" aria-hidden="true">
                <defs>
                  <clipPath id="gp-horizon-header"><rect width="1024" height="1024" rx="224" /></clipPath>
                </defs>
                <g clipPath="url(#gp-horizon-header)">
                  <rect width="1024" height="1024" fill="#5E6AD2" />
                  <circle cx="512" cy="465" r="135" fill="#FFFFFF" />
                  <rect y="465" width="1024" height="559" fill="#0A0F1F" />
                  <path fill="#FFFFFF" d="M300 1024C260 940 390 880 570 825C760 760 760 705 640 665C550 630 520 560 500 465H540C550 540 590 602 680 638C820 700 820 790 660 850C520 910 575 980 670 1024H300Z" />
                </g>
                <rect width="1024" height="1024" rx="224" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="32" />
              </svg>
              <span className="app-header-brand-name">Glide-Path</span>
            </NavLink>

            {showHamburger && (
              <button
                type="button"
                className="app-header-hamburger"
                onClick={() => setMenuOpen(prev => !prev)}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span className={`app-header-hamburger-icon ${menuOpen ? 'app-header-hamburger-icon--open' : ''}`} />
              </button>
            )}
          </div>

          <div className={`app-header-right ${showHamburger && menuOpen ? 'app-header-right--open' : ''} ${showHamburger ? 'app-header-right--hamburger' : ''}`}>
            <nav className="app-header-nav" aria-label="Main navigation">
              <NavLink to="/pricing" className="app-header-nav-link" onClick={closeMenu}>Pricing</NavLink>
              {isAuthenticated && (
                <>
                  <NavLink to="/tools" className="app-header-nav-link" onClick={closeMenu}>Tools</NavLink>
                  {/* NavLink only matches its own path, so "My Plan" never lit
                      up on /plans/:id/projection or the comparison views —
                      match the whole plan context by location instead. */}
                  <NavLink
                    to="/plan"
                    className={`app-header-nav-link${isPlanContext ? ' active' : ''}`}
                    onClick={closeMenu}
                  >My Plan</NavLink>
                  <NavLink
                    to="/account/api-keys"
                    className={`app-header-nav-link${isApiContext ? ' active' : ''}`}
                    onClick={closeMenu}
                  >API Keys</NavLink>
                </>
              )}
            </nav>

            <div className="app-header-auth">
              {isAuthenticated ? (
                <button onClick={handleLogout} className="app-header-btn app-header-btn--ghost">
                  Log out
                </button>
              ) : (
                <>
                  <NavLink to="/login" className="app-header-btn app-header-btn--ghost" onClick={closeMenu}>Sign In</NavLink>
                  <a href="/#beta-request" className="app-header-btn app-header-btn--primary" onClick={closeMenu}>Request Access</a>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
